import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useNavigate } from "react-router";

import { parseDeepLink } from "@/lib/deepLinks";

/** Window in which a repeat of the same path is treated as the same open. */
const DEDUPE_WINDOW_MS = 2000;

/**
 * Route Universal Links (iOS) / App Links (Android) into the router instead of
 * letting them bounce out to the browser.
 *
 * Two entry points, because `appUrlOpen` only reaches JS that is already
 * running: a warm open (app in background) fires the listener, while a cold
 * open (link launched the process) has already fired it by the time React
 * mounts — `getLaunchUrl()` recovers that one. Both can fire for the same
 * launch on Android, hence the dedupe.
 *
 * Route guards handle auth: an unauthenticated deep link lands on a guard,
 * which stores the path via `rememberRedirectAfterAuth` and bounces to login.
 *
 * Must be called inside the router. Native-only; a no-op on web.
 */
export function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let removeListener: (() => void) | undefined;
    let lastHandled: { path: string; at: number } | null = null;

    const handleUrl = (rawUrl: string) => {
      const path = parseDeepLink(rawUrl);
      if (!path) return;

      // Suppress the cold-start double-fire only. Re-opening the same link
      // later in the session is a real navigation and must still work.
      if (
        lastHandled?.path === path &&
        Date.now() - lastHandled.at < DEDUPE_WINDOW_MS
      ) {
        return;
      }
      lastHandled = { path, at: Date.now() };

      console.log("[DeepLink] Navigating to:", path);
      navigate(path);
    };

    const setup = async () => {
      const listener = await CapacitorApp.addListener(
        "appUrlOpen",
        ({ url }) => {
          console.log("[DeepLink] appUrlOpen:", url);
          handleUrl(url);
        },
      );

      if (cancelled) {
        listener.remove();
        return;
      }
      removeListener = () => listener.remove();

      const launch = await CapacitorApp.getLaunchUrl();
      if (!cancelled && launch?.url) {
        console.log("[DeepLink] Launch URL:", launch.url);
        handleUrl(launch.url);
      }
    };

    setup().catch(err => {
      console.error("[DeepLink] Setup failed:", err);
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [navigate]);
}
